/**
 * TenantCatalogTab — matrice tenant × catalogo (MOD-2-D + MOD-2-E)
 *
 * Mostra l'intero catalogo (modules + features) con per ciascuna voce
 * lo stato per il tenant selezionato:
 *  - Attivo nel pacchetto base (read-only, deriva da tier)
 *  - Attivo come add-on (toggle on/off via setTenantAddons)
 *  - Disponibile (mostrato come "attivabile")
 *  - Coming soon (disabled)
 *  - Deprecated (nascosto/grigio)
 *
 * Toggle modifica `tenant.modules.addon[]` via callable `setTenantAddons`
 * (region europe-west1).
 *
 * Gli STRUMENTI A CANONE (centralino, agente WhatsApp, servizi a consumo) dal 10/10/2026 non si scrivono più a mano:
 * `setTenantAddons` li fa passare dal percorso unico della vetrina (`cambiaStatoStrumento`), quindi entrano
 * nell'abbonamento, e la risposta dice com'è andata (`strumento_a_canone`). Uno strumento che si accende solo con la
 * sua CONSEGNA (il centralino: canone dal giorno in cui il numero risponde, atlante cap. 264) non ha l'interruttore
 * per accenderlo: porta a «Assegna il numero all'azienda» nella scheda Strumenti. Da MOD-2-E la callable carica dinamicamente la
 * whitelist degli add-on validi da `mkt_catalog_modules` + `mkt_catalog_features`
 * — quindi qualsiasi voce stable/beta del catalogo e' attivabile/disattivabile
 * dalla console (no piu' limitazione ai soli 3 marketplace).
 *
 * @since 2026-05-17 (MOD-2-D, esteso a tutti gli add-on con MOD-2-E)
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Typography, Paper, Stack, Tabs, Tab, Switch, FormControlLabel,
  CircularProgress, Alert, Snackbar, TextField, MenuItem, Tooltip,
  Divider, alpha, useTheme,
} from '@mui/material';
import {
  ViewModule, Inventory2, CheckCircle, Schedule, Science,
  Block, Lock, Search,
} from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import type { StrumentoACanone } from '@shared/strumenti/listino';
import { leggiListino } from '../services/strumentiService';
import { leggiErrore } from '../services/consegneService';
import { ultimoGiorno } from './strumenti/testiStrumenti';
import {
  messaggioEsitoStrumento, messaggioStrumentoDaConsegnare, type EsitoStrumentoACanone,
} from './telefono/testiConsegna';
import { loadCatalog } from '@shared/catalog/catalogService.client';
import type { CatalogSnapshot } from '@shared/catalog/catalogService';
import { SoftChip, type SoftChipTone } from './SoftChip';
import type {
  ModuleDescriptor, FeatureDescriptor, CatalogItemStatus, ModuleCategory, FeatureCategory,
} from '@shared/types';
import type { TenantConsoleListItem } from '../services/tenantService';

interface Props {
  tenant: TenantConsoleListItem;
  onChange: () => Promise<void>;
  /** Apre la scheda «Strumenti» dell'azienda, dove si consegna il centralino («Assegna il numero»). */
  onApriStrumenti?: () => void;
}

const giorno = (iso: string) => ultimoGiorno(Date.parse(iso));

type CatalogTab = 'modules' | 'features';

const STATUS_TONE: Record<CatalogItemStatus, SoftChipTone> = {
  stable: 'success',
  beta: 'warning',
  coming_soon: 'default',
  deprecated: 'error',
};

const STATUS_LABELS: Record<CatalogItemStatus, string> = {
  stable: 'Stable',
  beta: 'Beta',
  coming_soon: 'In arrivo',
  deprecated: 'Deprecato',
};

const STATUS_ICONS: Record<CatalogItemStatus, React.ReactElement> = {
  stable: <CheckCircle sx={{ fontSize: 14 }} />,
  beta: <Science sx={{ fontSize: 14 }} />,
  coming_soon: <Schedule sx={{ fontSize: 14 }} />,
  deprecated: <Block sx={{ fontSize: 14 }} />,
};

const MODULE_CATEGORY_LABELS: Record<ModuleCategory, string> = {
  core: 'Core (incluso)',
  premium: 'Premium',
  marketplace: 'Marketplace',
  integration: 'Integration',
};

const FEATURE_CATEGORY_LABELS: Record<FeatureCategory, string> = {
  base: 'Base (inclusa nel modulo)',
  premium: 'Premium',
  integration: 'Integration',
};

export const TenantCatalogTab: React.FC<Props> = ({ tenant, onChange, onApriStrumenti }) => {
  const theme = useTheme();
  const [tab, setTab] = useState<CatalogTab>('modules');
  const [catalog, setCatalog] = useState<CatalogSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'available'>('all');
  const [saving, setSaving] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; severity: 'success' | 'error' | 'info' | 'warning'; message: string; versoStrumenti?: boolean }>({ open: false, severity: 'info', message: '' });
  /** Il listino degli strumenti a canone: dice quali voci passano dall'abbonamento e quali dalla consegna. */
  const [listino, setListino] = useState<Map<string, StrumentoACanone>>(new Map());

  useEffect(() => {
    leggiListino()
      .then(setListino)
      .catch((e) => console.error('[catalogo] listino degli strumenti non letto', e));
  }, []);

  useEffect(() => {
    // Force-reload del catalogo all'apertura: la cache 5min puo' mostrare
    // capability rinominate/eliminate altrove (es. script di migrazione),
    // e il super_admin che opera qui deve sempre vedere lo stato reale.
    setLoading(true);
    loadCatalog(db, { force: true })
      .then(setCatalog)
      .catch((e) => setError(e instanceof Error ? e.message : 'Errore catalogo'))
      .finally(() => setLoading(false));
  }, []);

  // Stato per-tenant: dato un id, ritorna l'origine dell'attivazione
  const activeAddons = useMemo(() => new Set<string>((tenant.modules?.addon ?? []) as string[]), [tenant.modules]);
  const baseModules = useMemo(() => new Set<string>((tenant.modules?.base ?? []) as string[]), [tenant.modules]);

  // Funzioni del piano che non riguardano i prodotti dell'azienda (09/10/2026, le scrive il server da
  // «Prodotti disponibili» e dai `prodotti_pertinenti` del catalogo): non compaiono, salvo aggiunta qui.
  const nonPertinenti = useMemo(() => new Set<string>((tenant.modules?.non_pertinenti ?? []) as string[]), [tenant.modules]);

  const computeState = (id: string): { active: boolean; source: 'base' | 'addon' | 'non_pertinente' | 'none' } => {
    if (baseModules.has(id) && !nonPertinenti.has(id)) return { active: true, source: 'base' };
    if (activeAddons.has(id)) return { active: true, source: 'addon' };
    if (baseModules.has(id)) return { active: false, source: 'non_pertinente' };
    return { active: false, source: 'none' };
  };

  const filtered = useMemo(() => {
    if (!catalog) return [] as Array<ModuleDescriptor | FeatureDescriptor>;
    const source = tab === 'modules' ? catalog.modules : catalog.features;
    return source.filter((item) => {
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${item.label} ${item.description} ${item.id}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filterActive === 'active') {
        const { active } = computeState(item.id);
        if (!active) return false;
      }
      if (filterActive === 'available') {
        const { active } = computeState(item.id);
        if (active) return false;
      }
      return true;
    });
  }, [catalog, tab, search, filterActive, baseModules, activeAddons]);

  const handleToggle = async (itemId: string, currentlyActive: boolean) => {
    setSaving(itemId);
    try {
      // Pattern add/remove (no replace): la callable rilegge la lista corrente
      // di Firestore sotto transazione e applica solo l'azione richiesta.
      // Cosi' una UI con vista stale (cache 5min del catalogo o altre
      // sessioni concorrenti) non puo' "perdere" addon non visibili.
      const call = httpsCallable<
        { tenant_id: string; action: 'add' | 'remove'; addon_id: string },
        { success: true; strumento_a_canone?: EsitoStrumentoACanone }
      >(functions, 'setTenantAddons');
      const risposta = await call({
        tenant_id: tenant.id,
        action: currentlyActive ? 'remove' : 'add',
        addon_id: itemId,
      });
      const strumento = risposta.data.strumento_a_canone;
      if (strumento) {
        // Uno strumento a canone: com'è andata davvero (acceso nell'abbonamento, gratis fino a…, serve il pagamento).
        const m = messaggioEsitoStrumento(listino.get(itemId)?.etichetta ?? itemId, strumento, giorno);
        setSnack({ open: true, severity: m.gravita, message: m.testo });
      } else {
        setSnack({ open: true, severity: 'success', message: `${itemId} ${currentlyActive ? 'disattivato' : 'attivato'}` });
      }
      await onChange();
    } catch (e) {
      const daConsegnare = messaggioStrumentoDaConsegnare(leggiErrore(e), listino.get(itemId)?.etichetta ?? itemId);
      setSnack(
        daConsegnare
          ? { open: true, severity: 'info', message: daConsegnare, versoStrumenti: true }
          : { open: true, severity: 'error', message: e instanceof Error ? e.message : 'Errore' },
      );
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error || !catalog) return <Alert severity="error">{error || 'Errore caricamento'}</Alert>;

  return (
    <Box>
      {/* Header con tabs + filtri */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setSearch(''); setFilterActive('all'); }} sx={{ px: 1 }}>
          <Tab value="modules" icon={<ViewModule fontSize="small" />} iconPosition="start" label={`Moduli (${catalog.modules.length})`} />
          <Tab value="features" icon={<Inventory2 fontSize="small" />} iconPosition="start" label={`Features (${catalog.features.length})`} />
        </Tabs>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Cerca (nome, id, descrizione)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
          />
          <TextField
            select label="Stato attivazione" value={filterActive} onChange={(e) => setFilterActive(e.target.value as 'all' | 'active' | 'available')}
            size="small" sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">Tutti</MenuItem>
            <MenuItem value="active">Solo attivi</MenuItem>
            <MenuItem value="available">Solo disponibili</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      {/* Lista voci catalogo per quel tenant */}
      <Paper variant="outlined">
        <Stack divider={<Divider />}>
          {filtered.map((item) => {
            const state = computeState(item.id);
            const strumento = listino.get(item.id);
            // Si accende solo con la sua consegna: l'interruttore può spegnerlo, non accenderlo.
            const daConsegnare = !!strumento?.richiede_consegna && !state.active;
            const isToggleable = item.status !== 'coming_soon' && item.status !== 'deprecated' && !daConsegnare;
            const isComing = item.status === 'coming_soon';
            const isDeprecated = item.status === 'deprecated';
            const category = (item as ModuleDescriptor | FeatureDescriptor).category;
            const categoryLabel = tab === 'modules'
              ? MODULE_CATEGORY_LABELS[category as ModuleCategory]
              : FEATURE_CATEGORY_LABELS[category as FeatureCategory];

            return (
              <Box
                key={item.id}
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  bgcolor: state.active
                    ? alpha(theme.palette.success.main, 0.04)
                    : isDeprecated ? alpha(theme.palette.text.primary, 0.02) : 'transparent',
                  transition: 'background-color 0.2s',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {item.label}
                    </Typography>
                    <SoftChip label={item.id} tone="default" mono />
                    <SoftChip label={categoryLabel} tone={state.source === 'base' ? 'primary' : 'info'} />
                    <SoftChip
                      label={STATUS_LABELS[item.status]}
                      tone={STATUS_TONE[item.status]}
                      icon={STATUS_ICONS[item.status]}
                    />
                    {item.type === 'feature' && (
                      <SoftChip label={`↳ ${(item as FeatureDescriptor).parent_module}`} tone="default" mono />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                  {(item as ModuleDescriptor).pricing_hint && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                      💰 {(item as ModuleDescriptor).pricing_hint}
                    </Typography>
                  )}
                  {strumento && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {daConsegnare
                        ? 'Strumento a canone che si accende con la sua consegna: il canone parte dal giorno in cui funziona.'
                        : 'Strumento a canone: acceso e spento passano dall’abbonamento dell’azienda, come dalla vetrina.'}
                    </Typography>
                  )}
                  {daConsegnare && onApriStrumenti && (
                    <Button size="small" onClick={onApriStrumenti} sx={{ mt: 0.5, px: 0, textTransform: 'none' }}>
                      Vai ad «Assegna il numero»
                    </Button>
                  )}
                  {state.source === 'non_pertinente' && (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                      Nel piano {tenant.tier ?? ''}, ma non riguarda i prodotti che l'azienda vende: non compare.
                      Attivala come aggiunta per mostrarla comunque.
                    </Typography>
                  )}
                  {state.source === 'base' && (
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                      <Lock sx={{ fontSize: 12, color: 'text.disabled' }} />
                      <Typography variant="caption" color="text.disabled">
                        Incluso nel tier {tenant.tier ?? 'full'} (non disattivabile)
                      </Typography>
                    </Stack>
                  )}
                </Box>

                <Box sx={{ flexShrink: 0, minWidth: 140, textAlign: 'right' }}>
                  {state.source === 'base' ? (
                    <SoftChip label="ATTIVO (base)" tone="success" icon={<CheckCircle sx={{ fontSize: 14 }} />} />
                  ) : isComing ? (
                    <SoftChip label="IN ARRIVO" tone="default" icon={<Schedule sx={{ fontSize: 14 }} />} />
                  ) : isDeprecated ? (
                    <SoftChip label="DEPRECATO" tone="error" icon={<Block sx={{ fontSize: 14 }} />} />
                  ) : (
                    <Tooltip
                      title={
                        daConsegnare
                          ? 'Si accende con la sua consegna, dalla scheda Strumenti'
                          : isToggleable
                            ? (state.active ? 'Disattiva add-on per questo tenant' : 'Attiva add-on per questo tenant')
                            : 'Voce non attivabile (coming soon o deprecata)'
                      }
                    >
                      <span>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={state.active}
                              onChange={() => handleToggle(item.id, state.active)}
                              disabled={!isToggleable || saving === item.id}
                              color="primary"
                            />
                          }
                          label={
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              {state.active ? 'Attivo' : 'Non attivo'}
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          })}
          {filtered.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.disabled">Nessuna voce trovata con i filtri attuali</Typography>
            </Box>
          )}
        </Stack>
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          action={
            snack.versoStrumenti && onApriStrumenti ? (
              <Button color="inherit" size="small" onClick={() => { setSnack((s) => ({ ...s, open: false })); onApriStrumenti(); }}>
                Apri Strumenti
              </Button>
            ) : undefined
          }
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
